import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@autop/ui/src/button';
import { Field } from '@autop/ui/src/field';

import { ConsentAgreement, type ConsentType, hasAllRequiredConsents } from '@/entities/consent';
import { useSession } from '@/entities/session';
import { setAccessToken, useTRPC } from '@/shared/api';
import { rememberLastSignin } from '@/shared/lib/last-signin';
import { signinLanding } from '@/shared/lib/signin-landing';
import {
  clearSocialHandoff,
  PROVIDER_LABELS,
  type SocialProvider,
} from '@/shared/lib/social-handoff';

/** members.name 의 컬럼 폭. 넘치면 서버가 거절한다. */
const MAX_NAME_LENGTH = 20;

interface SocialSignupFormProps {
  /** 어느 제공사로 왔는지. 기억하지 못했으면 null 이고, 그때는 제공사를 말하지 않는다. */
  provider: SocialProvider | null;
  /** 가입을 마치고 돌아갈 사이트 안의 경로. */
  next: string;
}

/**
 * 소셜 가입 완료 — 이 화면이 있는 이유는 **약관 동의** 하나다.
 *
 * 카카오·네이버가 대신 받아 줄 수 없고 서버가 필수 3종을 강제한다. 그래서 소셜이라고
 * 건너뛸 수 없다.
 *
 * 이메일과 제공자 계정은 **보내지 않는다.** 둘 다 서버가 티켓(httpOnly 쿠키)에서 꺼낸다 —
 * 클라이언트가 실어 보내면 가입 요청이 남의 이메일로 계정을 만들 수 있다.
 *
 * 이름을 제공자 닉네임으로 미리 채워 주지도 않는다. 그 값은 티켓 안에 있어 브라우저가
 * 읽을 수 없고, 소셜 닉네임은 20자를 넘길 수도 있다.
 */
export function SocialSignupForm({ provider, next }: SocialSignupFormProps): ReactElement {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const providerLabel = provider === null ? null : PROVIDER_LABELS[provider];
  const signIn = useSession((state) => state.signIn);

  const [name, setName] = useState('');
  const [granted, setGranted] = useState<ReadonlySet<ConsentType>>(new Set());
  const [isMailSent, setIsMailSent] = useState(false);

  const completeSignup = useMutation(
    trpc.auth.social.completeSignup.mutationOptions({
      onSuccess: (result) => {
        // 성공했으면 어느 갈래든 소셜 흐름은 여기서 끝난다 — 쪽지를 남기지 않는다.
        clearSocialHandoff();

        // 제공자가 이메일을 확인해 주지 않은 경우다 — 인증 메일이 나갔고 세션은 없다.
        if (result.accessToken === null) {
          setIsMailSent(true);
          return;
        }

        setAccessToken(result.accessToken);
        signIn(result.member);
        // 여기서만 기억한다 — 인증 메일이 나간 갈래는 아직 로그인이 아니다.
        if (provider !== null) {
          rememberLastSignin(provider);
        }
        // 방금 만든 회원이라 온보딩은 언제나 미완료다. 목적지 규칙은 로그인과 한 곳에서 정한다.
        void navigate(signinLanding(next, false), { replace: true });
      },
    }),
  );

  if (isMailSent) {
    return (
      <div className="u-container flex max-w-[30rem] flex-col items-center gap-5 py-24 text-center">
        <h1 className="text-[1.375rem] font-bold tracking-[-0.04em]">메일함을 확인해 주세요</h1>
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          가입을 마치려면 이메일 인증이 필요합니다. 보내 드린 메일의 링크를 눌러 주세요.
        </p>
      </div>
    );
  }

  const canSubmit = name.trim().length > 0 && hasAllRequiredConsents(granted);

  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[26rem] flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-[1.625rem] font-bold tracking-[-0.04em]">가입 마무리</h1>
          {providerLabel === null ? null : (
            <p className="text-[14px] text-muted-foreground">{providerLabel}로 가입합니다</p>
          )}
        </div>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;

            // 필수 3종은 서버 스키마가 `true` 리터럴로 받는다 — 위 검사를 지나야만
            //   이 객체를 만들 수 있으므로, 검사를 빠뜨린 경로가 생기지 않는다.
            completeSignup.mutate({
              name: name.trim(),
              consents: {
                AGE_OVER_14: true,
                TERMS_OF_SERVICE: true,
                PRIVACY_POLICY: true,
                MARKETING_EMAIL: granted.has('MARKETING_EMAIL'),
              },
            });
          }}
        >
          <Field
            id="social-signup-name"
            label="닉네임"
            placeholder="오톱러"
            required
            maxLength={MAX_NAME_LENGTH}
            value={name}
            onChange={(event) => setName(event.target.value)}
            hint={`${MAX_NAME_LENGTH}자 이내로 적어 주세요`}
          />

          <ConsentAgreement granted={granted} onChange={setGranted} idPrefix="social-signup" />

          {completeSignup.error === null ? null : (
            <p role="alert" className="text-[13px] text-destructive">
              {completeSignup.error.message}
            </p>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!canSubmit || completeSignup.isPending}
          >
            동의하고 가입하기
          </Button>
        </form>
      </div>
    </div>
  );
}
